import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography, Button, useTheme, Switch, alpha, Tooltip, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { PrimaryGrid } from '@ui/components/common/Grid';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
import EmailConfiguration from './emailConfiguration';
import EmailSettings from './emailConfiguration/EmailSettings';
import RunEmailDialog from './emailConfiguration/RunEmailDialog';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getEventEmailTemplatesAsync, toggleEmailTemplatesByEventAndTypeAsync } from '@application/features/emailTemplates/emailTemplatesThunk';
import { sendEmailTemplateAsync, getEmailTemplateScheduledStatusAsync } from '@application/features/emailTemplateSettings/emailTemplateSettingsThunk';
import { EmailTemplateType } from '@application/features/emailTemplates/models/email-template-type';
import CustomDialog from '@ui/components/common/Dialog';
import { centeredRowStack } from '@ui/style/style';
import { toast } from 'react-toastify';
import { captureConfigureEmailPageScroll } from '@application/utilities/email-editor-scroll-guard';
import { convertUtcToLocal } from '@application/utilities/date-time-formater';
const emailTypeHasScheduleSettings = (templateType) => templateType !== EmailTemplateType.WelcomeThankYou;
const PreferencesTab = ({ viewMode = 'list', selectedTemplate = null, onOpenConfigure, onOpenSettings }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { eventEmailTemplates } = useAppSelector((state) => state.emailTemplates);
    const VDKEventsForEventDetails = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const { scheduledStatusList } = useAppSelector((state) => state.emailTemplateSettings);
    const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
    const [switchTargetRow, setSwitchTargetRow] = useState(null);
    const [runDialogOpen, setRunDialogOpen] = useState(false);
    const [runTargetRow, setRunTargetRow] = useState(null);
    useEffect(() => {
        if (eventId && viewMode === 'list') {
            dispatch(getEventEmailTemplatesAsync({ eventId }));
            dispatch(getEmailTemplateScheduledStatusAsync(eventId));
        }
    }, [dispatch, eventId, i18n.language, viewMode]);
    const isScheduleConfigured = (row) => {
        const scheduledStatus = scheduledStatusList?.find((item) => String(item.templateType) === String(row.templateType));
        return !!scheduledStatus?.scheduledDate;
    };
    const templateRows = useMemo(() => {
        if (!eventEmailTemplates)
            return [];
        const isSponsorship = !!VDKEventsForEventDetails?.partnerId;
        const groups = new Map();
        eventEmailTemplates.forEach((item) => {
            if (!isSponsorship && item.templateType === EmailTemplateType.Ticket)
                return;
            const key = String(item.templateType);
            if (!groups.has(key))
                groups.set(key, []);
            groups.get(key).push(item);
        });
        return Array.from(groups.entries()).map(([, items]) => {
            const first = items[0];
            const latestDate = items
                .map((i) => i.lastModifiedDate)
                .filter(Boolean)
                .sort()
                .reverse()[0];
            const formattedDate = latestDate
                ? new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                }).format(new Date(latestDate))
                : '';
            const typeKey = String(first.templateType);
            const scheduledStatus = scheduledStatusList?.find((item) => String(item.templateType) === typeKey);
            return {
                id: String(first.templateType),
                templateId: first?.id || '',
                title: first.name,
                languagesCount: first.languagesCount || 0,
                lastUpdated: formattedDate,
                active: first.isActive,
                templateType: first.templateType,
                sentScheduledEmail: scheduledStatus?.sentScheduledEmail ?? false,
                sentAt: scheduledStatus?.sentAt ?? null
            };
        });
    }, [eventEmailTemplates, VDKEventsForEventDetails?.partnerId, scheduledStatusList, t]);
    const hasUnconfiguredActiveSchedules = useMemo(() => {
        return templateRows.some((row) => row.active && emailTypeHasScheduleSettings(row.templateType) && !isScheduleConfigured(row));
    }, [templateRows, scheduledStatusList]);
    const handleToggleActive = async (row) => {
        if (!eventId)
            return;
        await dispatch(toggleEmailTemplatesByEventAndTypeAsync({
            eventId,
            templateType: row.templateType,
            isActive: !row.active
        }))
            .unwrap()
            .then(() => {
            toast.success(row.active
                ? t('events.eventDetails.preferencesTab.deactivatedSuccess')
                : t('events.eventDetails.preferencesTab.activatedSuccess'));
            dispatch(getEventEmailTemplatesAsync({ eventId }));
        })
            .catch(() => {
            toast.error(t('events.eventDetails.preferencesTab.toggleError', 'Failed to update template status'));
        });
    };
    const handleOpenEmailConfig = (row) => {
        captureConfigureEmailPageScroll();
        onOpenConfigure?.(row);
    };
    const handleOpenEmailSettings = (row) => {
        onOpenSettings?.(row);
    };
    const handleOpenRunDialog = (row) => {
        setRunTargetRow(row);
        setRunDialogOpen(true);
    };
    const handleConfirmRun = () => {
        if (!runTargetRow)
            return;
        dispatch(sendEmailTemplateAsync({ emailTemplateId: runTargetRow.templateId }))
            .unwrap()
            .then((response) => {
            if (response.isSuccess) {
                toast.success(response.message);
            }
            else {
                toast.error(response.message);
            }
        })
            .catch(() => {
            toast.error(t('events.eventDetails.preferencesTab.runTriggerError'));
        })
            .finally(() => {
            setRunDialogOpen(false);
            setRunTargetRow(null);
        });
    };
    const getEmailLanguageTooltip = (templateType) => {
        switch (templateType) {
            case EmailTemplateType.Invitation:
                return t('events.eventDetails.preferencesTab.invitationLanguageTooltip');
            case EmailTemplateType.Ticket:
                return t('events.eventDetails.preferencesTab.ticketLanguageTooltip');
            case EmailTemplateType.WelcomeThankYou:
                return t('events.eventDetails.preferencesTab.thankYouLanguageTooltip');
            case EmailTemplateType.ReInvitationReminder:
                return t('events.eventDetails.preferencesTab.reInvitationLanguageTooltip');
            case EmailTemplateType.RegisteredReminder:
                return t('events.eventDetails.preferencesTab.registeredReminderLanguageTooltip');
            case EmailTemplateType.FinalDetails:
                return t('events.eventDetails.preferencesTab.finalDetailsLanguageTooltip');
            case EmailTemplateType.PostEventThankYou:
                return t('events.eventDetails.preferencesTab.postEventThankYouLanguageTooltip');
            default:
                return null;
        }
    };
    if (viewMode === 'configure' && selectedTemplate) {
        return (_jsx(EmailConfiguration, { templateTitle: selectedTemplate.title, templateType: selectedTemplate.templateType }, selectedTemplate.templateId ?? selectedTemplate.templateType));
    }
    if (viewMode === 'settings' && selectedTemplate) {
        return (_jsx(EmailSettings, { templateTitle: selectedTemplate.title, templateType: selectedTemplate.templateType, emailTemplateId: selectedTemplate.templateId }));
    }
    return (_jsxs(Box, { children: [hasUnconfiguredActiveSchedules && (_jsx(Alert, { severity: 'warning', sx: { mb: 2 }, children: t('events.eventDetails.preferencesTab.scheduledEmailNotConfiguredWarning') })), _jsx(PrimaryGrid, { columns: [
                    {
                        field: 'title',
                        headerName: t('events.eventDetails.preferencesTab.title'),
                        flex: 1,
                        minWidth: 220,
                        renderCell: (params) => {
                            const row = params.row;
                            const showScheduleStatus = row.active && emailTypeHasScheduleSettings(row.templateType);
                            const configured = isScheduleConfigured(row);
                            return (_jsx(Box, { display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, children: _jsx(Typography, { variant: 'body2', noWrap: true, children: row.title }) }));
                        }
                    },
                    {
                        field: 'languagesCount',
                        headerName: t('events.eventDetails.preferencesTab.languages'),
                        flex: 1,
                        minWidth: 120,
                        renderCell: (params) => {
                            const row = params.row;
                            const languageTooltip = getEmailLanguageTooltip(row.templateType);
                            return (_jsxs(Box, { display: 'grid', gridTemplateColumns: '32px auto', gap: 0.5, alignItems: 'center', children: [_jsx(Box, { display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, minHeight: 32, borderRadius: '500px', sx: { backgroundColor: alpha(theme.palette.info.main, 0.08) }, color: theme.palette.info.dark, children: row.languagesCount }), languageTooltip && (_jsx(InfoIconTooltip, { variant: 'dark', size: 'small', title: languageTooltip, icon: _jsx(Icon, { icon: 'hugeicons:information-circle', color: theme.palette.info.dark, fontSize: 12, style: { pointerEvents: 'none' } }) }))] }));
                        }
                    },
                    {
                        field: 'lastUpdated',
                        headerName: t('events.eventDetails.preferencesTab.lastUpdated'),
                        flex: 1,
                        minWidth: 200
                    },
                    {
                        field: 'scheduledStatus',
                        headerName: t('events.eventDetails.preferencesTab.scheduledStatus'),
                        flex: 1,
                        minWidth: 180,
                        renderCell: (params) => {
                            const row = params.row;
                            const hasScheduleSettings = emailTypeHasScheduleSettings(row.templateType);
                            if (!hasScheduleSettings) {
                                return null;
                            }
                            if (row.sentScheduledEmail) {
                                // convertUtcToLocal returns DD/MM/YYYY HH:mm — reformat to YYYY-MM-DD HH:mm
                                const localSentAt = row.sentAt ? convertUtcToLocal(row.sentAt) : null;
                                const sentAtFormatted = localSentAt
                                    ? (() => {
                                        const [datePart, timePart] = localSentAt.split(' ');
                                        const [dd, mm, yyyy] = datePart.split('/');
                                        return `${yyyy}-${mm}-${dd} ${timePart}`;
                                    })()
                                    : '';
                                return (_jsx(Tooltip, { title: `${t('events.eventDetails.preferencesTab.emailScheduledSent')}: ${sentAtFormatted}`, children: _jsx(Chip, { label: t('events.eventDetails.preferencesTab.scheduledEmailSent'), size: 'small', variant: 'soft', color: 'success' }) }));
                            }
                            const isScheduled = isScheduleConfigured(row);
                            const scheduledStatus = scheduledStatusList?.find((item) => String(item.templateType) === String(row.templateType));
                            const scheduledDateFormatted = scheduledStatus?.scheduledDate
                                ? new Date(scheduledStatus.scheduledDate).toISOString().split('T')[0]
                                : '';
                            return (_jsx(Tooltip, { title: isScheduled ? `${t('events.eventDetails.preferencesTab.scheduledDateTooltip')}: ${scheduledDateFormatted}` : null, children: _jsx(Chip, { label: isScheduled
                                        ? t('events.eventDetails.preferencesTab.scheduled')
                                        : t('events.eventDetails.preferencesTab.notScheduled'), size: 'small', variant: 'soft', color: isScheduled ? 'info' : 'warning' }) }));
                        }
                    },
                    {
                        field: 'active',
                        headerName: t('events.eventDetails.preferencesTab.active', 'Active'),
                        flex: 1,
                        minWidth: 120,
                        renderCell: (params) => (_jsx(Box, { onClick: (event) => event.stopPropagation(), children: _jsx(Switch, { checked: params.row.active, onChange: () => {
                                    setSwitchTargetRow(params.row);
                                    setSwitchDialogOpen(true);
                                } }) }))
                    },
                    {
                        field: 'runTrigger',
                        headerName: t('events.eventDetails.preferencesTab.runTrigger'),
                        flex: 0.5,
                        minWidth: 120,
                        renderCell: (params) => {
                            const row = params.row;
                            if (row.templateType === EmailTemplateType.WelcomeThankYou ||
                                row.templateType === EmailTemplateType.Ticket) {
                                return null;
                            }
                            return (_jsx(Box, { onClick: (event) => event.stopPropagation(), children: _jsx(Button, { variant: 'outlined', color: 'error', size: 'medium', disabled: !row.active, sx: {
                                        borderColor: alpha(theme.palette.error.main, 0.24),
                                        color: theme.palette.error.main
                                    }, onClick: () => handleOpenRunDialog(row), children: t('events.eventDetails.preferencesTab.run') }) }));
                        }
                    },
                    {
                        field: 'actions',
                        headerName: t('events.eventDetails.preferencesTab.actions'),
                        flex: 1,
                        headerAlign: 'center',
                        minWidth: 280,
                        align: 'center',
                        renderCell: (params) => {
                            const row = params.row;
                            const hideSettings = row.templateType === EmailTemplateType.WelcomeThankYou ||
                                row.templateType === EmailTemplateType.Ticket;
                            return (_jsxs(Box, { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%', sx: { minWidth: 260 }, onClick: (event) => event.stopPropagation(), children: [_jsx(Box, { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', children: !hideSettings && (_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'medium', onClick: () => handleOpenEmailSettings(row), children: t('events.eventDetails.preferencesTab.settings') })) }), _jsx(Box, { display: 'flex', justifyContent: 'flex-start', alignItems: 'center', children: _jsx(Button, { variant: 'outlined', color: 'inherit', size: 'medium', onClick: () => handleOpenEmailConfig(row), children: t('events.eventDetails.preferencesTab.configureEmail', 'Configure Email') }) })] }));
                        }
                    }
                ], rows: templateRows, showNewHeaderControls: true, showHeaderControls: false, customButton: _jsx(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, children: _jsx(Box, { children: _jsx(Typography, { variant: 'h4', children: t('events.eventDetails.preferencesTab.countTemplates', {
                                count: templateRows.length
                            }) }) }) }) }), switchDialogOpen && switchTargetRow && (_jsx(CustomDialog, { open: switchDialogOpen, title: t('events.eventDetails.preferencesTab.confirmStatusChange'), onClose: () => setSwitchDialogOpen(false), actions: _jsxs(Box, { sx: centeredRowStack, width: '100%', gap: 1, children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', onClick: () => setSwitchDialogOpen(false), children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'primary', fullWidth: true, onClick: () => {
                                handleToggleActive(switchTargetRow);
                                setSwitchDialogOpen(false);
                            }, children: t('actions.confirm') })] }), children: _jsx(Box, { padding: 3, children: _jsx(Typography, { variant: 'body1', children: t('events.eventDetails.preferencesTab.toggleConfirm') }) }) })), _jsx(RunEmailDialog, { open: runDialogOpen, onClose: () => setRunDialogOpen(false), onConfirm: handleConfirmRun, templateType: runTargetRow?.templateType, recipientGroups: runTargetRow
                    ? scheduledStatusList?.find((item) => String(item.templateType) === String(runTargetRow.templateType))?.recipientGroups
                    : undefined })] }));
};
export default PreferencesTab;
